import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { CreditCard, CheckCircle2, AlertTriangle, Trash2, ExternalLink, Loader2, Link as LinkIcon, KeyRound, Landmark } from "lucide-react";
import { toast } from "sonner";
import { usePlaidLink } from "react-plaid-link";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";

const GATEWAYS = [
  {
    key: "stripe",
    name: "Stripe",
    brand: "bg-[#635BFF]",
    tag: "Cards · ACH · Apple/Google Pay",
    learn: "https://dashboard.stripe.com/connect",
  },
  {
    key: "paypal",
    name: "PayPal",
    brand: "bg-[#003087]",
    tag: "PayPal balance · Venmo",
    learn: "https://developer.paypal.com/dashboard",
  },
  {
    key: "bank",
    name: "Pay by Bank",
    brand: "bg-emerald-700",
    tag: "ACH · Direct deposit · Wire",
    learn: "https://en.wikipedia.org/wiki/ABA_routing_transit_number",
  },
];

export default function PaymentGatewaysSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // gateway key

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payment-gateways");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load gateways");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Handle Stripe OAuth bounce-back (backend redirects with ?stripe_connected=1)
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_connected") === "1") {
      toast.success("Stripe account connected");
      params.delete("stripe_connected");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const disconnect = async (gw) => {
    if (!window.confirm(`Disconnect ${gw.toUpperCase()}? Tenants will no longer be able to pay rent through it.`)) return;
    try {
      await api.delete(`/payment-gateways/${gw}`);
      toast.success(`${gw.charAt(0).toUpperCase() + gw.slice(1)} disconnected`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Disconnect failed");
    }
  };

  const startStripeOAuth = async () => {
    try {
      const { data } = await api.get("/payment-gateways/stripe/oauth/start");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "OAuth not available — use manual keys for now.");
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="payment-gateways-section">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 bg-indigo-600 text-white flex items-center justify-center rounded-sm">
          <CreditCard className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Payment Gateways</h2>
          <p className="text-sm text-slate-500">
            Connect your own Stripe and PayPal accounts. Rent payments flow directly to you — RealtyVault never holds tenant funds.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-6 text-center">Loading gateways…</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {GATEWAYS.map((g) => {
            const it = items.find((i) => i.gateway === g.key) || { gateway: g.key, configured: false, oauth_ready: false };
            return (
              <GatewayCard
                key={g.key}
                meta={g}
                state={it}
                onEdit={() => setEditing(g.key)}
                onDisconnect={() => disconnect(g.key)}
                onConnectOAuth={g.key === "stripe" ? startStripeOAuth : null}
              />
            );
          })}
        </div>
      )}

      <StripeManualDialog
        open={editing === "stripe"}
        onClose={() => setEditing(null)}
        existing={items.find((i) => i.gateway === "stripe")}
        onSaved={() => { setEditing(null); load(); }}
      />
      <PayPalManualDialog
        open={editing === "paypal"}
        onClose={() => setEditing(null)}
        existing={items.find((i) => i.gateway === "paypal")}
        onSaved={() => { setEditing(null); load(); }}
      />
      <BankManualDialog
        open={editing === "bank"}
        onClose={() => setEditing(null)}
        existing={items.find((i) => i.gateway === "bank")}
        onSaved={() => { setEditing(null); load(); }}
      />
    </section>
  );
}

function GatewayCard({ meta, state, onEdit, onDisconnect, onConnectOAuth }) {
  const verified = !!state.last_verified_at;
  const err = state.last_error;
  const isBank = meta.key === "bank";
  return (
    <div className={`border ${state.configured ? "border-slate-300" : "border-dashed border-slate-200"} rounded-sm p-5 flex flex-col`} data-testid={`gateway-card-${meta.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className={`${meta.brand} text-white font-bold h-10 w-10 rounded-sm flex items-center justify-center text-xs tracking-widest`}>
            {isBank ? <Landmark className="w-4 h-4" /> : meta.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{meta.name}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{meta.tag}</div>
          </div>
        </div>
        {state.configured ? (
          isBank ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm" data-testid={`gateway-status-${meta.key}`}>
              <CheckCircle2 className="w-3 h-3" /> Active
            </span>
          ) : verified ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm" data-testid={`gateway-status-${meta.key}`}>
              <CheckCircle2 className="w-3 h-3" /> Verified
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm" data-testid={`gateway-status-${meta.key}`}>
              <AlertTriangle className="w-3 h-3" /> Unverified
            </span>
          )
        ) : (
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 bg-slate-50 border border-slate-200 px-2 py-1 rounded-sm">Not connected</span>
        )}
      </div>

      <div className="mt-4 space-y-1.5 text-xs font-mono text-slate-600 flex-1">
        {state.configured ? (
          <>
            {state.label && <div>Label: <span className="text-slate-900">{state.label}</span></div>}
            {/* Bank-specific fields */}
            {isBank && state.account_holder_name && <div>Holder: <span className="text-slate-900">{state.account_holder_name}</span></div>}
            {isBank && state.bank_name && <div>Bank: <span className="text-slate-900">{state.bank_name}</span></div>}
            {isBank && state.routing_number && <div>Routing: <span className="text-slate-900">{state.routing_number}</span></div>}
            {isBank && state.account_last4 && <div>Account: <span className="text-slate-900">••••{state.account_last4}{state.account_type ? ` · ${state.account_type}` : ""}</span></div>}
            {/* Stripe / PayPal fields */}
            {!isBank && state.connected_account_id && <div>Account: <span className="text-slate-900">{state.connected_account_id}</span></div>}
            {!isBank && state.merchant_id && <div>Merchant: <span className="text-slate-900">{state.merchant_id}</span></div>}
            {!isBank && state.paypal_email && <div>Email: <span className="text-slate-900">{state.paypal_email}</span></div>}
            {!isBank && state.secret_preview && <div>Secret: <span className="text-slate-900">{state.secret_preview}</span></div>}
            {err && <div className="text-red-600">Error: {err}</div>}
          </>
        ) : (
          <div className="text-slate-400">
            {isBank
              ? "Add your bank routing + account so tenants can pay rent by ACH/wire transfer directly to your account. Verified via the standard ABA checksum."
              : `Connect your ${meta.name} account so tenants can pay rent directly to you.`}{" "}
            <a href={meta.learn} target="_blank" rel="noreferrer" className="underline hover:text-slate-700 inline-flex items-center gap-1">
              {isBank ? "What is a routing number?" : `Open ${meta.name} dashboard`} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center gap-2 pt-3 border-t border-slate-100">
        {!state.configured ? (
          <>
            {onConnectOAuth && state.oauth_ready && (
              <button onClick={onConnectOAuth} className="btn-primary-dark text-sm flex items-center gap-2" data-testid={`connect-${meta.key}-oauth`}>
                <LinkIcon className="w-4 h-4" /> Connect with {meta.name}
              </button>
            )}
            <button onClick={onEdit} className={onConnectOAuth && state.oauth_ready ? "btn-outline-dark text-sm flex items-center gap-2" : "btn-primary-dark text-sm flex items-center gap-2"} data-testid={`connect-${meta.key}-manual`}>
              {isBank ? <Landmark className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
              {isBank ? "Add bank account" : "Enter API keys"}
            </button>
          </>
        ) : (
          <>
            <button onClick={onEdit} className="btn-outline-dark text-sm" data-testid={`edit-${meta.key}`}>{isBank ? "Update bank" : "Update keys"}</button>
            <button onClick={onDisconnect} className="ml-auto text-slate-500 hover:text-red-600 p-1.5" data-testid={`disconnect-${meta.key}`} title="Disconnect">
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function StripeManualDialog({ open, onClose, existing, onSaved }) {
  const [form, setForm] = useState({ mode: "manual", secret_key: "", publishable_key: "", connected_account_id: "", label: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        mode: "manual",
        secret_key: "",
        publishable_key: existing?.publishable_key || "",
        connected_account_id: existing?.connected_account_id || "",
        label: existing?.label || "",
      });
    }
  }, [open, existing]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.secret_key && !existing?.configured) {
      toast.error("Paste your Stripe secret key to connect.");
      return;
    }
    setBusy(true);
    try {
      const payload = { mode: "manual", label: form.label || "My Stripe" };
      if (form.secret_key.trim()) payload.secret_key = form.secret_key.trim();
      if (form.publishable_key.trim()) payload.publishable_key = form.publishable_key.trim();
      if (form.connected_account_id.trim()) payload.connected_account_id = form.connected_account_id.trim();
      const { data } = await api.put("/payment-gateways/stripe", payload);
      toast.success(data.verified ? "Stripe verified & connected" : "Saved (unverified)");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="stripe-manual-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Connect Stripe (Manual)</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <p className="text-xs text-slate-500">
            Grab your keys from{" "}
            <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="underline hover:text-slate-900">dashboard.stripe.com/apikeys</a>.
            Secret key is verified on save and stored encrypted at rest — only a masked preview is shown after.
          </p>
          <Field label="Secret key (sk_live_… or sk_test_…)">
            <input
              type="password"
              data-testid="stripe-secret-input"
              className="input-clean w-full font-mono text-xs"
              placeholder={existing?.secret_preview ? `current: ${existing.secret_preview} — paste new to replace` : "sk_live_••••"}
              value={form.secret_key}
              onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
              autoComplete="off"
            />
          </Field>
          <Field label="Publishable key (optional, pk_live_…)">
            <input
              type="text"
              data-testid="stripe-pk-input"
              className="input-clean w-full font-mono text-xs"
              placeholder="pk_live_…"
              value={form.publishable_key}
              onChange={(e) => setForm({ ...form, publishable_key: e.target.value })}
            />
          </Field>
          <Field label="Connected account id (optional, acct_…)">
            <input
              type="text"
              data-testid="stripe-acct-input"
              className="input-clean w-full font-mono text-xs"
              placeholder="acct_… (auto-filled from /v1/account if left blank)"
              value={form.connected_account_id}
              onChange={(e) => setForm({ ...form, connected_account_id: e.target.value })}
            />
          </Field>
          <Field label="Label (optional)">
            <input type="text" data-testid="stripe-label-input" className="input-clean w-full" placeholder="e.g. Rentals LLC" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Field>
          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary-dark flex items-center gap-2" data-testid="stripe-save-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {existing?.configured ? "Update" : "Connect"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PayPalManualDialog({ open, onClose, existing, onSaved }) {
  const [form, setForm] = useState({ mode: "manual", client_id: "", client_secret: "", merchant_id: "", paypal_email: "", label: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        mode: "manual",
        client_id: existing?.client_id || "",
        client_secret: "",
        merchant_id: existing?.merchant_id || "",
        paypal_email: existing?.paypal_email || "",
        label: existing?.label || "",
      });
    }
  }, [open, existing]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.merchant_id && !form.paypal_email && !form.client_id && !existing?.configured) {
      toast.error("Enter at least your PayPal merchant id or email.");
      return;
    }
    setBusy(true);
    try {
      const payload = { mode: "manual", label: form.label || "My PayPal" };
      if (form.client_id.trim())     payload.client_id = form.client_id.trim();
      if (form.client_secret.trim()) payload.client_secret = form.client_secret.trim();
      if (form.merchant_id.trim())   payload.merchant_id = form.merchant_id.trim();
      if (form.paypal_email.trim())  payload.paypal_email = form.paypal_email.trim();
      const { data } = await api.put("/payment-gateways/paypal", payload);
      toast.success(data.verified ? "PayPal verified & connected" : "Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="paypal-manual-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Connect PayPal (Manual)</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <p className="text-xs text-slate-500">
            Grab your client credentials from{" "}
            <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer" className="underline hover:text-slate-900">developer.paypal.com</a>.
            Client id + secret are verified on save against PayPal's OAuth endpoint. Supplying just merchant id / email also works (unverified).
          </p>
          <Field label="Merchant id (optional)">
            <input type="text" data-testid="paypal-merchant-input" className="input-clean w-full font-mono text-xs" placeholder="E.g. ABC123MERCHANT" value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })} />
          </Field>
          <Field label="PayPal email (optional)">
            <input type="email" data-testid="paypal-email-input" className="input-clean w-full" placeholder="payments@yourcompany.com" value={form.paypal_email} onChange={(e) => setForm({ ...form, paypal_email: e.target.value })} />
          </Field>
          <Field label="REST API client id (optional)">
            <input type="text" data-testid="paypal-client-id-input" className="input-clean w-full font-mono text-xs" placeholder="AcFg…" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} />
          </Field>
          <Field label="REST API client secret (optional)">
            <input type="password" data-testid="paypal-client-secret-input" className="input-clean w-full font-mono text-xs" autoComplete="off" placeholder="EL…" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} />
          </Field>
          <Field label="Label (optional)">
            <input type="text" data-testid="paypal-label-input" className="input-clean w-full" placeholder="e.g. Rentals" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Field>
          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary-dark flex items-center gap-2" data-testid="paypal-save-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {existing?.configured ? "Update" : "Connect"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BankManualDialog({ open, onClose, existing, onSaved }) {
  const [form, setForm] = useState({
    account_holder_name: "", bank_name: "", routing_number: "",
    account_number: "", account_type: "checking", instructions: "", label: "",
  });
  const [busy, setBusy] = useState(false);
  const [plaidReady, setPlaidReady] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        account_holder_name: existing?.account_holder_name || "",
        bank_name: existing?.bank_name || "",
        routing_number: existing?.routing_number || "",
        account_number: "",
        account_type: existing?.account_type || "checking",
        instructions: existing?.instructions || "",
        label: existing?.label || "",
      });
      // Check whether Plaid is configured on the server
      api.get("/payment-gateways")
        .then(({ data }) => {
          const bank = (data.items || []).find((i) => i.gateway === "bank");
          setPlaidReady(!!bank?.oauth_ready);
        })
        .catch(() => setPlaidReady(false));
    }
  }, [open, existing]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.account_holder_name.trim()) { toast.error("Account holder name is required."); return; }
    if (!form.routing_number.trim()) { toast.error("Routing number is required."); return; }
    if (!form.account_number.trim() && !existing?.account_last4) {
      toast.error("Account number is required."); return;
    }
    setBusy(true);
    try {
      const payload = {
        account_holder_name: form.account_holder_name.trim(),
        bank_name: form.bank_name.trim() || undefined,
        routing_number: form.routing_number.replace(/\D/g, ""),
        account_type: form.account_type,
        instructions: form.instructions.trim() || undefined,
        label: form.label.trim() || undefined,
      };
      if (form.account_number.trim()) {
        payload.account_number = form.account_number.replace(/\D/g, "");
      }
      await api.put("/payment-gateways/bank", payload);
      toast.success(existing?.configured ? "Bank details updated" : "Pay by Bank connected");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="bank-manual-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Pay by Bank — ACH details</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <p className="text-xs text-slate-500">
            Tenants will see these deposit instructions on their portal. Your full account number is stored server-side and never shown back —
            only the last 4 digits appear in the UI.
            Routing numbers are validated with the standard ABA checksum.
          </p>

          {plaidReady ? (
            <PlaidConnectButton
              onLinked={(linked) => {
                toast.success(`Connected ${linked.bank_name} ••••${linked.account_last4}`);
                onSaved();
              }}
            />
          ) : (
            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 leading-relaxed" data-testid="plaid-not-configured-hint">
              💡 <b>Plaid</b> connect-your-bank flow is available once the platform admin adds <code className="font-mono bg-white border border-slate-200 px-1 rounded">PLAID_CLIENT_ID</code> + <code className="font-mono bg-white border border-slate-200 px-1 rounded">PLAID_SECRET_SANDBOX</code> to <code className="font-mono bg-white border border-slate-200 px-1 rounded">backend/.env</code>. Enter routing/account manually below for now.
            </div>
          )}
          <Field label="Account holder name *">
            <input type="text" data-testid="bank-holder-input" className="input-clean w-full" required placeholder="Your LLC or legal name" value={form.account_holder_name} onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })} />
          </Field>
          <Field label="Bank name">
            <input type="text" data-testid="bank-name-input" className="input-clean w-full" placeholder="E.g. Wells Fargo" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Routing number (9 digits) *">
              <input
                type="text"
                inputMode="numeric"
                data-testid="bank-routing-input"
                className="input-clean w-full font-mono text-xs"
                required
                placeholder="121000248"
                maxLength={11}
                value={form.routing_number}
                onChange={(e) => setForm({ ...form, routing_number: e.target.value.replace(/\D/g, "").slice(0, 9) })}
              />
            </Field>
            <Field label="Account type">
              <select
                data-testid="bank-account-type"
                className="input-clean w-full"
                value={form.account_type}
                onChange={(e) => setForm({ ...form, account_type: e.target.value })}
              >
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </Field>
          </div>
          <Field label={existing?.account_last4 ? `Account number (current: ••••${existing.account_last4} — leave blank to keep)` : "Account number *"}>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              data-testid="bank-account-input"
              className="input-clean w-full font-mono text-xs"
              placeholder={existing?.account_last4 ? "••••••••••••" : "Your checking/savings account number"}
              maxLength={17}
              value={form.account_number}
              onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, "").slice(0, 17) })}
            />
          </Field>
          <Field label="Wire / ACH instructions for tenants">
            <textarea
              data-testid="bank-instructions-input"
              className="input-clean w-full text-sm"
              rows={3}
              placeholder="e.g. Please include the tenant name + unit in the memo line. Allow 3 business days for ACH settlement."
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            />
          </Field>
          <Field label="Label (optional)">
            <input type="text" data-testid="bank-label-input" className="input-clean w-full" placeholder="e.g. Ops Checking" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Field>
          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary-dark flex items-center gap-2" data-testid="bank-save-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {existing?.configured ? "Update" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PlaidConnectButton({ onLinked }) {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exchanging, setExchanging] = useState(false);

  const fetchToken = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payment-gateways/bank/plaid/link-token");
      setLinkToken(data.link_token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start Plaid session.");
    } finally {
      setLoading(false);
    }
  };

  const onSuccess = async (public_token /*, metadata */) => {
    setExchanging(true);
    try {
      const { data } = await api.post("/payment-gateways/bank/plaid/exchange", { public_token });
      onLinked({ bank_name: data.bank_name, account_last4: data.account_last4 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Plaid exchange failed.");
    } finally {
      setExchanging(false);
      setLinkToken(null);
    }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: () => { setLinkToken(null); },
  });

  // Auto-open as soon as the token is loaded + SDK is ready
  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  const busy = loading || exchanging || (linkToken && !ready);

  return (
    <div className="rounded-sm border border-emerald-200 bg-emerald-50/60 p-3 flex items-center justify-between gap-3" data-testid="plaid-connect-card">
      <div className="min-w-0">
        <div className="text-sm font-bold text-emerald-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Landmark className="w-4 h-4" /> Connect via Plaid <span className="text-[10px] font-mono uppercase tracking-widest bg-white border border-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded-sm">Recommended</span>
        </div>
        <p className="text-xs text-emerald-800 mt-0.5">
          Skip manual entry. Plaid verifies your routing + account number directly with 12,000+ banks.
        </p>
      </div>
      <button
        type="button"
        onClick={fetchToken}
        disabled={busy}
        data-testid="plaid-connect-button"
        className="btn-primary-dark text-sm flex items-center gap-2 shrink-0"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
        {exchanging ? "Verifying…" : loading ? "Opening…" : "Connect bank"}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
